/**
 * Provider block for invoice-based superbills (public superbill page, invoice
 * detail header, invoice PDF).
 *
 * The provider snapshot is written at invoice create/edit time in two places:
 * `visit_notes[<encounter_id>].provider` (only when clinical notes are
 * included) and `line_items[].metadata.provider` (always, for encounter lines).
 * Readers resolve from either source so the header does not depend on the
 * clinical-notes checkbox. A superbill that spans more than one provider
 * shows no provider block, matching the provider-side superbill PDFs.
 */

export interface SuperbillProvider {
  name?: string | null;
  npi?: string | null;
  credentials?: string | null;
  designation?: string | null;
  license_number?: string | null;
}

interface InvoiceLike {
  visit_notes?: Record<string, { provider?: SuperbillProvider | null } | null> | null;
  line_items?: Array<{ metadata?: { provider?: SuperbillProvider | null } | null } | null> | null;
}

function providerKey(p: SuperbillProvider): string {
  const npi = String(p.npi || '').trim();
  if (npi) return `npi:${npi}`;
  return `name:${String(p.name || '').trim().toLowerCase()}`;
}

function hasContent(p: SuperbillProvider | null | undefined): p is SuperbillProvider {
  return !!p && !!(String(p.name || '').trim() || String(p.npi || '').trim());
}

/**
 * Single provider for the invoice's superbill header, or null when the
 * invoice carries no provider snapshot or more than one distinct provider.
 */
export function resolveInvoiceProvider(invoice: InvoiceLike | null | undefined): SuperbillProvider | null {
  if (!invoice) return null;
  const candidates: SuperbillProvider[] = [];
  for (const note of Object.values(invoice.visit_notes || {})) {
    if (hasContent(note?.provider)) candidates.push(note!.provider!);
  }
  for (const line of invoice.line_items || []) {
    if (hasContent(line?.metadata?.provider)) candidates.push(line!.metadata!.provider!);
  }
  if (candidates.length === 0) return null;
  const distinct = new Map<string, SuperbillProvider>();
  for (const p of candidates) {
    const key = providerKey(p);
    if (!distinct.has(key)) distinct.set(key, p);
  }
  if (distinct.size !== 1) return null;
  return distinct.values().next().value ?? null;
}

type LineItemLike = { encounter_id?: string | null; metadata?: Record<string, any> | null; [k: string]: any };

/**
 * Write-time: copy the visit's provider snapshot onto an encounter line
 * (`metadata.provider`), or remove it when the org hides provider info.
 * Non-encounter lines are returned unchanged.
 */
export function applyLineProviderSnapshot<T extends LineItemLike>(
  line: T,
  visitNotes: Record<string, { provider?: SuperbillProvider | null } | null> | null | undefined,
  showProviderInfo: boolean,
): T {
  if (!line?.encounter_id) return line;
  const provider = showProviderInfo ? visitNotes?.[line.encounter_id]?.provider : null;
  if (hasContent(provider)) {
    return { ...line, metadata: { ...(line.metadata || {}), provider } };
  }
  if (line.metadata && 'provider' in line.metadata) {
    const { provider: _omit, ...rest } = line.metadata;
    return { ...line, metadata: rest };
  }
  return line;
}

/** Write-time: visit notes without their provider blocks (org hides provider info). */
export function stripVisitNoteProviders<T extends Record<string, any>>(visitNotes: T | null | undefined): T | null | undefined {
  if (!visitNotes) return visitNotes;
  const out: Record<string, any> = {};
  for (const [encId, note] of Object.entries(visitNotes)) {
    if (note && typeof note === 'object' && 'provider' in note) {
      const { provider: _omit, ...rest } = note as Record<string, any>;
      out[encId] = rest;
    } else {
      out[encId] = note;
    }
  }
  return out as T;
}
